import { useRouter, useSearchParams } from 'next/navigation';
import NextImage from 'next/image';
import {
  Button,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  UncontrolledDropdown
} from 'reactstrap';
import { useState } from 'react';
import { Layout } from '../components/Layout';
import Meta from '../components/Meta';
import { Heading } from '../components/Heading';
import { Bounded } from '../components/Bounded';
import Paginate from '../components/Paginate';
import { LatestOrRelatedItem, PublicationItem } from '../slices/List';
import COUNTRIES from '../lib/countries';

// prismic document type to page title mapping
const PAGE_TITLES = {
  page: { name: 'Pages', title: 'All pages', search: 'Search pages' },
  news: { name: 'News', title: 'All news', search: 'Search news' },
  // eslint-disable-next-line prettier/prettier
  project: { name: 'Projects', title: 'All projects', search: 'Search projects' },
  // eslint-disable-next-line prettier/prettier
  publication: { name: 'Publications', title: 'All publications', search: 'Search publications' }
};

const SearchPage = ({ results, navigation, settings }) => {
  const searchParams = useSearchParams();
  const type = searchParams.get('type') || 'news';
  const tags = searchParams.get('tags')?.split(',') || [];
  const q = searchParams.get('q');

  const title =
    tags.length === 0 && q === null
      ? PAGE_TITLES[type]?.title
      : PAGE_TITLES[type]?.search;

  return (
    <Layout
      // alternateLanguages={page.alternate_languages} // TODO show all available languages
      navigation={navigation}
      settings={settings}
      page={{}}>
      <Meta meta={{ title }} settings={settings} />
      <Bounded as="section">
        <Heading as="h1" className="mb-3">
          {title}
        </Heading>
        <div className="py-4">
          <Form>
            {/* only show search box with q parameter present */}
            {q !== null && <SearchBox type={type} />}
            <div>
              {Object.entries(PAGE_TITLES).map(([key, { name }]) => (
                <RadioItem label={name} type={key} key={key} />
              ))}
              {['project', 'publication'].includes(type) && <CountrySelect />}
              {tags.map((tag, i) => (
                <Button
                  key={tag}
                  outline
                  size="sm"
                  className="ms-2"
                  href={searchRoute({
                    q,
                    type,
                    // eslint-disable-next-line prettier/prettier
                    tags: tags.reduce((r, t, j) => (j === i ? r : r.concat([t])), [])
                  })}>
                  {tag} ⨯
                </Button>
              ))}
            </div>
          </Form>
        </div>
        <div className="row">
          {type === 'publication'
            ? results.results.map((item) => (
                <PublicationItem item={item} key={item.id} />
              ))
            : results.results.map((item) => (
                <LatestOrRelatedItem item={item} key={item.id} />
              ))}
        </div>
        <div className="justify-content-center d-flex">
          <Paginate
            current={results.page}
            total={results.total_results_size}
            perPage={results.results_per_page}
          />
        </div>
        {results.results.length === 0 && <div>No results found.</div>}
      </Bounded>
    </Layout>
  );
};

export default SearchPage;

function SearchBox({ type, props }) {
  const params = useSearchParams();
  const [q, setQ] = useState(params.get('q'));
  const router = useRouter();

  return (
    <FormGroup {...props}>
      <InputGroup>
        <InputGroupText>
          <NextImage
            src="/images/search-magnifier-grey.svg"
            width={25}
            height={25}
            alt=""
          />
        </InputGroupText>
        <Input
          name="q"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <Button
          color="primary"
          onClick={() => router.push(searchRoute({ q, type }))}>
          <NextImage
            src="/images/right-white.svg"
            width={10}
            height={20}
            alt="Search"
          />
        </Button>
      </InputGroup>
    </FormGroup>
  );
}

function RadioItem({ type, label }) {
  const params = useSearchParams();
  const pageType = params.get('type') || 'news';
  const q = params.get('q');
  const router = useRouter();
  return (
    <FormGroup check inline>
      <Input
        id={type}
        onChange={() => router.push(searchRoute({ q, type }))}
        name="type"
        value={pageType}
        checked={type === pageType}
        type="radio"
      />{' '}
      <Label for={type} check>
        {label}
      </Label>
    </FormGroup>
  );
}

function CountrySelect() {
  const params = useSearchParams();
  const type = params.get('type') || 'news';
  const q = params.get('q');
  const country = params.get('country');
  const selected_country = COUNTRIES[country];
  const router = useRouter();
  return (
    <UncontrolledDropdown
      active={!!country}
      size="sm"
      className="d-inline-block ms-2">
      <DropdownToggle caret color="outline-secondary">
        {selected_country && selected_country.flag}{' '}
        {selected_country ? selected_country.name : 'Country'}{' '}
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem onClick={() => router.push(searchRoute({ q, type }))}>
          All countries
        </DropdownItem>
        {Object.entries(COUNTRIES).map(([code, c]) => (
          <DropdownItem
            key={code}
            onClick={() =>
              router.push(searchRoute({ q, type, country: code }))
            }>
            {c.flag} {c.name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

function searchRoute({ q, type, tags, country }) {
  const searchParams = new URLSearchParams();
  searchParams.set('type', type);
  if (q !== null) searchParams.set('q', q);
  if (['project', 'publication'].includes(type) && country)
    searchParams.set('country', country);
  if (tags && tags.length > 0) searchParams.set('tags', tags.join(','));
  return `/search?${searchParams.toString()}`;
}
